import { useCallback, useEffect, useRef, useState } from "react"
import { useAppMessage, useDaily } from "./callTransport"
import { meetings, type MeetingPollPanel } from "services/api/meetingsClient"
import { createGuestSessionId } from "../shared/guestSession"

const REQUEST = "wisefriend.poll-admission-request"
const GRANT = "wisefriend.poll-admission-grant"
type AdmissionMessage = { type: string; meetingId: string; requestId: string; participantToken?: string; panel?: MeetingPollPanel }

/** Only the owner who can see an admitted Daily participant may endorse their pending panel token. */
export function useMeetingPollAdmission({ meetingId, panel, enabled }: { meetingId: string; panel?: MeetingPollPanel; enabled: boolean }) {
  const daily = useDaily()
  const [grant, setGrant] = useState<{ source: string; panel: MeetingPollPanel } | null>(null)
  const requestId = useRef(createGuestSessionId())
  const pending = useRef(new Set<string>())
  const mounted = useRef(true)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])
  const resolved = grant?.source === panel?.panel_token ? grant?.panel : panel
  const onAppMessage = useCallback((event: { fromId?: string; data?: AdmissionMessage }) => {
    const data = event.data
    if (!enabled || !daily || !panel || !event.fromId || data?.meetingId !== meetingId) return
    const participants = daily.participants()
    const sender = participants[event.fromId]
    if (!sender || sender.local) return
    if (data.type === GRANT && sender.owner && data.requestId === requestId.current && panel.pending_admission && data.panel?.panel_token && !data.panel.pending_admission) {
      setGrant({ source: panel.panel_token, panel: data.panel })
      return
    }
    if (data.type !== REQUEST || !participants.local?.owner || panel.role !== "presenter" || !data.participantToken || pending.current.has(event.fromId)) return
    const target = event.fromId
    pending.current.add(target)
    // Daily defaults user_id to session_id for tokenless waiting-room joins.
    // That transport identity is not a WiseFriend account/guest identifier.
    const stableUserId = sender.user_id && sender.user_id !== sender.session_id ? sender.user_id : undefined
    void meetings.admitMeetingPollParticipant(meetingId, panel.panel_token, data.participantToken, stableUserId).then(next => {
      if (mounted.current && daily.participants()[target]) daily.sendAppMessage({ type: GRANT, meetingId, requestId: data.requestId, panel: next }, target)
    }).catch(() => { /* A later request retries transient failures without disrupting the call. */ }).finally(() => pending.current.delete(target))
  }, [daily, enabled, meetingId, panel])
  useAppMessage({ onAppMessage })
  useEffect(() => {
    if (!enabled || !daily || !panel?.pending_admission || !resolved?.pending_admission) return
    const request = () => {
      Object.values(daily.participants()).filter(person => person.owner && !person.local).forEach(owner => {
        try { daily.sendAppMessage({ type: REQUEST, meetingId, requestId: requestId.current, participantToken: panel.panel_token }, owner.session_id) } catch { /* Reconnect retries. */ }
      })
    }
    request()
    const timer = window.setInterval(request, 5000)
    return () => window.clearInterval(timer)
  }, [daily, enabled, meetingId, panel, resolved?.pending_admission])
  return resolved?.pending_admission ? undefined : resolved
}
